import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { IngestTicket, loadWB, saveWB, getInbox, pushInbox, parseCSV, applyThresholdAlerts } from './weighbridge'
import { useNotifications } from './notifications'

type Ctx = {
  running: boolean
  inbox: IngestTicket[]
  start: () => void
  stop: () => void
  setInbox: (rows: IngestTicket[]) => void
}

const WBContext = createContext<Ctx|null>(null)

export function WeighbridgeProvider({children}:{children:React.ReactNode}){
  const [running, setRunning] = useState(true)
  const [inbox, setInboxState] = useState(getInbox())
  const { push } = useNotifications()

  function setInbox(rows: IngestTicket[]){ setInboxState(rows); pushInbox(rows) }

  useEffect(()=>{
    if (!running) return
    let timer: any
    const loop = async () => {
      const cfg = loadWB()
      if (cfg.mode === 'API' && cfg.api?.url) {
        // demo: we won't actually call external; you can plug in your API here
        // const res = await fetch(cfg.api.url, { headers: { Authorization: `Bearer ${cfg.api.token||''}` }})
        // const rows: IngestTicket[] = await res.json()
        // For now, do nothing in API mode.
      } else if (cfg.mode === 'CSV' && cfg.csv?.sample) {
        const rows = parseCSV(cfg.csv.sample)
        if (rows.length) {
          const all = [...rows, ...getInbox()].slice(0, 500)
          setInbox(all)
          rows.forEach(r => {
            const n = applyThresholdAlerts(r, cfg)
            if (n) push(n)
          })
        }
      }
      timer = setTimeout(loop, (cfg.intervalSec||10)*1000)
    }
    loop()
    return ()=> { if (timer) clearTimeout(timer) }
  }, [running])

  const value = useMemo(()=>({ running, inbox, start:()=>setRunning(true), stop:()=>setRunning(false), setInbox }), [running, inbox])
  return <WBContext.Provider value={value}>{children}</WBContext.Provider>
}

export function useWeighbridge(){ 
  const ctx = useContext(WBContext); 
  if(!ctx) throw new Error('useWeighbridge must be inside WeighbridgeProvider'); 
  return ctx 
}
